// CreatorApplication.jsx - Place in /workspaces/mcn-youtube-platform/mcn-frontend/src/components/CreatorApplication.jsx

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../config';
import './CreatorApplication.css';

const CreatorApplication = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const referralCode = searchParams.get('ref');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationality: '',
    currentLocation: '',
    
    // Channel Information
    channelName: '',
    channelUrl: '',
    contentNiche: '',
    yearsOfExperience: '',
    currentSubscribers: '',
    monthlyViews: '',
    
    // Content Strategy
    contentStrategy: '',
    uniqueValue: '',
    targetAudience: '',
    monetizationPlan: '',
    
    // Resources
    hasCamera: false,
    hasEditingSoftware: false,
    hasDedicatedSpace: false,
    internetSpeed: '',
    
    // Commitment
    hoursPerWeek: '',
    videosPerMonth: '',
    
    // Goals
    revenueGoal6Months: '',
    revenueGoal1Year: '',
    growthStrategy: '',
    
    // Agreement
    agreedToTerms: false,
    agreedToRevShare: false,
    exclusivityAgreed: false,
    backgroundCheck: false
  });

  const steps = [
    { id: 1, title: 'Personal Info', icon: '👤' },
    { id: 2, title: 'Channel Details', icon: '📺' },
    { id: 3, title: 'Content Strategy', icon: '📝' },
    { id: 4, title: 'Resources', icon: '🎥' },
    { id: 5, title: 'Commitment', icon: '⏰' },
    { id: 6, title: 'Agreement', icon: '✅' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = () => {
    const newErrors = {};
    
    switch (currentStep) {
      case 1:
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!formData.phone) newErrors.phone = 'Phone is required';
        break;
      case 2:
        if (!formData.channelName) newErrors.channelName = 'Channel name is required';
        if (!formData.channelUrl) newErrors.channelUrl = 'Channel URL is required';
        if (!formData.contentNiche) newErrors.contentNiche = 'Content niche is required';
        break;
      case 3:
        if (!formData.contentStrategy) newErrors.contentStrategy = 'Content strategy is required';
        if (!formData.targetAudience) newErrors.targetAudience = 'Target audience is required';
        break;
      case 4:
        if (!formData.internetSpeed) newErrors.internetSpeed = 'Internet speed is required';
        break;
      case 5:
        if (!formData.hoursPerWeek) newErrors.hoursPerWeek = 'Hours per week is required';
        if (!formData.videosPerMonth) newErrors.videosPerMonth = 'Videos per month is required';
        break;
      case 6:
        if (!formData.agreedToTerms) newErrors.agreedToTerms = 'You must agree to terms';
        if (!formData.agreedToRevShare) newErrors.agreedToRevShare = 'You must agree to revenue sharing';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        referredBy: referralCode,
        currentSubscribers: parseInt(formData.currentSubscribers) || 0,
        monthlyViews: parseInt(formData.monthlyViews) || 0,
        yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
        hoursPerWeek: parseInt(formData.hoursPerWeek) || 0,
        videosPerMonth: parseInt(formData.videosPerMonth) || 0,
        revenueGoal6Months: parseInt(formData.revenueGoal6Months) || 0,
        revenueGoal1Year: parseInt(formData.revenueGoal1Year) || 0,
      };
      
      const response = await axios.post(`${API_URL}/api/applications/create`, submitData);
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/application-success');
        }, 3000);
      }
} catch (error) {
  console.error('Submission error:', error);
  console.error('Error response:', error.response);
  if (error.response) {
    console.error('Status:', error.response.status);
    console.error('Data:', error.response.data);
    setErrors({ submit: error.response.data?.message || JSON.stringify(error.response.data?.errors) || 'Failed to submit application' });
  } else {
    console.error('Network error:', error.message);
    setErrors({ submit: 'Network error: ' + error.message });
  }
} finally {
  setLoading(false);
}
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={errors.firstName ? 'error' : ''}
                />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
              
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={errors.lastName ? 'error' : ''}
                />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
              
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label>Current Location</label>
                <input
                  type="text"
                  name="currentLocation"
                  value={formData.currentLocation}
                  onChange={handleInputChange}
                  placeholder="City, Country"
                />
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="step-content">
            <h3>Channel Details</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Channel Name *</label>
                <input
                  type="text"
                  name="channelName"
                  value={formData.channelName}
                  onChange={handleInputChange}
                  className={errors.channelName ? 'error' : ''}
                />
                {errors.channelName && <span className="error-text">{errors.channelName}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Channel URL *</label>
                <input
                  type="url"
                  name="channelUrl"
                  value={formData.channelUrl}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/@yourchannel"
                  className={errors.channelUrl ? 'error' : ''}
                />
                {errors.channelUrl && <span className="error-text">{errors.channelUrl}</span>}
              </div>
              
              <div className="form-group">
                <label>Content Niche *</label>
                <select
                  name="contentNiche"
                  value={formData.contentNiche}
                  onChange={handleInputChange}
                  className={errors.contentNiche ? 'error' : ''}
                >
                  <option value="">Select a niche</option>
                  <option value="Technology">Technology</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Education">Education</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Music">Music</option>
                  <option value="Sports">Sports</option>
                  <option value="Food">Food & Cooking</option>
                  <option value="Travel">Travel</option>
                  <option value="Fashion">Fashion & Beauty</option>
                  <option value="Health">Health & Fitness</option>
                  <option value="Business">Business & Finance</option>
                  <option value="Other">Other</option>
                </select>
                {errors.contentNiche && <span className="error-text">{errors.contentNiche}</span>}
              </div>
              
              <div className="form-group">
                <label>Years of Experience</label>
                <input
                  type="number"
                  name="yearsOfExperience"
                  value={formData.yearsOfExperience}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              
              <div className="form-group">
                <label>Current Subscribers</label>
                <input
                  type="number"
                  name="currentSubscribers"
                  value={formData.currentSubscribers}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              
              <div className="form-group">
                <label>Monthly Views (Average)</label>
                <input
                  type="number"
                  name="monthlyViews"
                  value={formData.monthlyViews}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="step-content">
            <h3>Content Strategy</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Content Strategy *</label>
                <textarea
                  name="contentStrategy"
                  value={formData.contentStrategy}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Describe your content creation strategy..."
                  className={errors.contentStrategy ? 'error' : ''}
                />
                {errors.contentStrategy && <span className="error-text">{errors.contentStrategy}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Unique Value Proposition</label>
                <textarea
                  name="uniqueValue"
                  value={formData.uniqueValue}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="What makes your content unique?"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Target Audience *</label>
                <textarea
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Describe your target audience..."
                  className={errors.targetAudience ? 'error' : ''}
                />
                {errors.targetAudience && <span className="error-text">{errors.targetAudience}</span>}
              </div>
              
              <div className="form-group full-width">
                <label>Monetization Plan</label>
                <textarea
                  name="monetizationPlan"
                  value={formData.monetizationPlan}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="How do you plan to monetize your content?"
                />
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="step-content">
            <h3>Resources & Equipment</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasCamera"
                      checked={formData.hasCamera}
                      onChange={handleInputChange}
                    />
                    <span>I have a camera/recording equipment</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasEditingSoftware"
                      checked={formData.hasEditingSoftware}
                      onChange={handleInputChange}
                    />
                    <span>I have video editing software</span>
                  </label>
                  
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasDedicatedSpace"
                      checked={formData.hasDedicatedSpace}
                      onChange={handleInputChange}
                    />
                    <span>I have a dedicated recording space</span>
                  </label>
                </div>
              </div>
              
              <div className="form-group full-width">
                <label>Internet Speed *</label>
                <select
                  name="internetSpeed"
                  value={formData.internetSpeed}
                  onChange={handleInputChange}
                  className={errors.internetSpeed ? 'error' : ''}
                >
                  <option value="">Select speed</option>
                  <option value="0-10">0-10 Mbps</option>
                  <option value="10-50">10-50 Mbps</option>
                  <option value="50-100">50-100 Mbps</option>
                  <option value="100+">100+ Mbps</option>
                </select>
                {errors.internetSpeed && <span className="error-text">{errors.internetSpeed}</span>}
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="step-content">
            <h3>Commitment & Goals</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Hours per Week *</label>
                <input
                  type="number"
                  name="hoursPerWeek"
                  value={formData.hoursPerWeek}
                  onChange={handleInputChange}
                  min="1"
                  className={errors.hoursPerWeek ? 'error' : ''}
                />
                {errors.hoursPerWeek && <span className="error-text">{errors.hoursPerWeek}</span>}
              </div>
              
              <div className="form-group">
                <label>Videos per Month *</label>
                <input
                  type="number"
                  name="videosPerMonth"
                  value={formData.videosPerMonth}
                  onChange={handleInputChange}
                  min="1"
                  className={errors.videosPerMonth ? 'error' : ''}
                />
                {errors.videosPerMonth && <span className="error-text">{errors.videosPerMonth}</span>}
              </div>
              
              <div className="form-group">
                <label>Revenue Goal (6 months)</label>
                <input
                  type="number"
                  name="revenueGoal6Months"
                  value={formData.revenueGoal6Months}
                  onChange={handleInputChange}
                  placeholder="₹"
                />
              </div>
              
              <div className="form-group">
                <label>Revenue Goal (1 year)</label>
                <input
                  type="number"
                  name="revenueGoal1Year"
                  value={formData.revenueGoal1Year}
                  onChange={handleInputChange}
                  placeholder="₹"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Growth Strategy</label>
                <textarea
                  name="growthStrategy"
                  value={formData.growthStrategy}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="How do you plan to grow your channel?"
                />
              </div>
            </div>
          </div>
        );
        
      case 6:
        return (
          <div className="step-content">
            <h3>Terms & Agreement</h3>
            <div className="agreement-section">
              <div className="agreement-box">
                <h4>Platform Terms</h4>
                <p>By joining Choice Network MCN, you agree to:</p>
                <ul>
                  <li>Follow platform guidelines and content policies</li>
                  <li>Maintain professional standards</li>
                  <li>Collaborate with the team</li>
                  <li>Provide accurate information</li>
                </ul>
              </div>
              
              <div className="agreement-box">
                <h4>Revenue Sharing</h4>
                <p>Standard revenue sharing model:</p>
                <ul>
                  <li>Creator: 55%</li>
                  <li>Platform: 45%</li>
                  <li>Monthly payouts</li>
                  <li>Transparent reporting</li>
                </ul>
              </div>
              
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreedToTerms"
                    checked={formData.agreedToTerms}
                    onChange={handleInputChange}
                  />
                  <span>I agree to the platform terms and conditions *</span>
                </label>
                {errors.agreedToTerms && <span className="error-text">{errors.agreedToTerms}</span>}
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreedToRevShare"
                    checked={formData.agreedToRevShare}
                    onChange={handleInputChange}
                  />
                  <span>I agree to the revenue sharing model *</span>
                </label>
                {errors.agreedToRevShare && <span className="error-text">{errors.agreedToRevShare}</span>}
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="exclusivityAgreed"
                    checked={formData.exclusivityAgreed}
                    onChange={handleInputChange}
                  />
                  <span>I understand the exclusivity requirements</span>
                </label>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="backgroundCheck"
                    checked={formData.backgroundCheck}
                    onChange={handleInputChange}
                  />
                  <span>I consent to background verification</span>
                </label>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="application-container">
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Application Submitted Successfully!</h2>
          <p>Thank you for applying to Choice Network MCN.</p>
          <p>We'll review your application and get back to you within 3-5 business days.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="application-container">
      <div className="application-header">
        <h1>Creator Application</h1>
        {referralCode && (
          <div className="referral-badge">
            Referred by: {referralCode}
          </div>
        )}
      </div>
      
      <div className="progress-bar">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`progress-step ${currentStep >= step.id ? 'active' : ''} ${currentStep === step.id ? 'current' : ''}`}
          >
            <div className="step-icon">{step.icon}</div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>
      
      <div className="application-form">
        {renderStepContent()}
        
        {errors.submit && (
          <div className="error-message">{errors.submit}</div>
        )}
        
        <div className="form-actions">
          {currentStep > 1 && (
            <button 
              className="btn btn-secondary" 
              onClick={handlePrevious}
              disabled={loading}
            >
              Previous
            </button>
          )}
          
          {currentStep < steps.length ? (
            <button 
              className="btn btn-primary" 
              onClick={handleNext}
              disabled={loading}
            >
              Next
            </button>
          ) : (
            <button 
              className="btn btn-success" 
              onClick={handleSubmit}
              disabled={loading || !formData.agreedToTerms || !formData.agreedToRevShare}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorApplication;